import { AnchorButton } from '../ui/button'
import { Icon } from '@blueprintjs/core'
import { Combobox } from '../ui/combobox'
// @ts-strict-ignore
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { axiosDestroy, axiosGet, axiosPost } from '../../util/api'
import { EventParentTypes, Filters, TableName } from '../../config'
import { useUser } from '../../hooks/useUser'
import { Menu, MenuItem } from '../ui/menu'
import { useLocation, useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import { Toasts } from '../toaster/Toast'
import { useTableHooksFromStore } from '../../packages/blueprint-components/src/useTableHooks'
import { format, formatDistanceToNow } from 'date-fns'
import { Tooltip } from '../ui/tooltip'
import type { State } from '../../reducers'
import { PreservingLink } from '../ui/PreservingLink'
import { EllipsisMenu } from '../../icons/icons'
import { NewPageLayout } from '../ui/NewPageLayout'
import type { EntityId } from '../../types/constants'
import { isNavV2Enabled } from '../../util/nav-version'
import { dryRunHref } from '../../util/promotions'
import { AgGridTable, DummyHeader, type AgGridColumnDef } from '../table2/AgGridTable'
import { Columns } from '../table2/ad_grid_columns'
import { TableStore } from '../table2/table_store'
import { useSavedFiltersStore } from '../../hooks/useSavedFiltersStore'
import { userStore } from '../../stores/user_store'
import { capitalCase } from 'change-case'
import { observer } from 'mobx-react-lite'

// ── Sort options ──────────────────────────────────────────────────────────────

const SORT_OPTIONS = [
    { label: 'Sort by…', value: '' },
    { label: 'Last changed ↓', value: '-last_change' },
    { label: 'Last changed ↑', value: 'last_change' },
    { label: 'Last run ↓', value: '-last_run' },
    { label: 'Last run ↑', value: 'last_run' },
    { label: 'Active rules ↓', value: '-active_rules_count' },
    { label: 'Active rules ↑', value: 'active_rules_count' },
    { label: 'Total rules ↓', value: '-rules_count' },
    { label: 'Total rules ↑', value: 'rules_count' },
    { label: 'Ad groups ↓', value: '-items_count' },
    { label: 'Ad groups ↑', value: 'items_count' },
    { label: 'Name A → Z', value: 'name' },
    { label: 'Name Z → A', value: '-name' },
    { label: 'Created ↓', value: '-created_at' },
    { label: 'Created ↑', value: 'created_at' },
]

const LAST_CHANGED_TOOLTIP = (
    <span className="inline-block max-w-[220px] leading-snug whitespace-normal">
        The last time an actual change was made on Amazon.
    </span>
)
const DEFAULT_STATS = { active: 0, paused: 0, ran_last_24h: 0, changed_last_24h: 0 }

// ── Columns ───────────────────────────────────────────────────────────────────

const columns: any[] = [
    {
        key: 'name',
        title: 'Name',
        sortable: true,
        cell: (promotion, extra) => <PromotionNameCell promotion={promotion} extra={extra} />,
    },
    {
        key: 'rules',
        title: (
            <Tooltip content="Active rules / Total rules" position="top">
                <span className="cursor-default">Rules</span>
            </Tooltip>
        ),
        sortable: true,
        sortField: 'active_rules_count',
        headerStyle: { justifyContent: 'flex-end' },
        style: { textAlign: 'right' },
        cell: (promotion) => {
            const active = promotion.active_rules_count ?? 0
            const total = promotion.rules_count ?? 0
            return (
                <span className="text-[14px] text-[#0E2947]">
                    {active}/{total}
                </span>
            )
        },
    },
    {
        key: 'items_count',
        title: 'Ad Groups',
        sortable: true,
        headerStyle: { justifyContent: 'flex-end' },
        style: { textAlign: 'right' },
        cell: (promotion) => (
            <span className="text-[14px] text-[#0E2947]">{promotion.items_count ?? 0}</span>
        ),
    },
    {
        key: 'last_run',
        title: 'Last Run',
        sortable: true,
        headerStyle: { justifyContent: 'flex-end' },
        style: { textAlign: 'right' },
        cell: (promotion) => <DateCell date={promotion.last_run} label="Last run" />,
    },
    {
        key: 'last_change',
        title: (
            <Tooltip content={LAST_CHANGED_TOOLTIP} position="top">
                <span className="cursor-default">Last Changed</span>
            </Tooltip>
        ),
        sortable: true,
        headerStyle: { justifyContent: 'flex-end' },
        style: { textAlign: 'right' },
        cell: (promotion) => <DateCell date={promotion.last_change} label="Last changed" />,
    },
    {
        key: 'actions',
        title: 'Actions',
        sortable: false,
        noRowClick: true,
        headerStyle: { justifyContent: 'flex-end' },
        cell: (promotion, extra) => <PromotionActions promotion={promotion} extra={extra} />,
    },
]

const filters = [Filters.RecipeEnabled]
const mutableFields = ['promotions:enabled', 'promotions:delete']

const promotionColumnSizing: Record<
    string,
    Pick<AgGridColumnDef, 'width' | 'minWidth' | 'flex'>
> = {
    name: { minWidth: 220, flex: 2 },
    rules: { width: 90 },
    items_count: { width: 115 },
    last_run: { width: 130 },
    last_change: { width: 145 },
    actions: { width: 72 },
}

const promotionColumnHeader = (key: string): string => capitalCase(key.replaceAll('_', ' '))

/** Pure adapter retains the old column metadata while giving AG Grid ownership. */
export const promotionColumnsToAgGrid = (
    legacyColumns: any[],
    mutateItems: (update: any) => void,
    reload: () => void,
): AgGridColumnDef<any>[] =>
    legacyColumns.map((column) => {
        const sizing = promotionColumnSizing[column.key]
        const sortable = column.sortable !== false && !column.noRowClick
        const renderCell = (params: any) =>
            column.cell(params.data, {
                screenshotMode: false,
                mutateItems,
                reload,
            })

        return {
            field: column.key,
            headerName:
                column.text_title ??
                (typeof column.title === 'string'
                    ? column.title
                    : promotionColumnHeader(column.key)),
            headerNode: typeof column.title === 'string' ? undefined : column.title,
            headerComponent: sortable ? DummyHeader : undefined,
            // Sorting a paginated response locally produces a misleading order
            // that only covers the loaded page. DummyHeader owns sort clicks and
            // writes the selected key to TableStore so the API sorts before paging.
            sortable: false,
            sortField: column.sortField ?? column.key,
            align: column.headerStyle?.justifyContent === 'flex-end' ? 'right' : 'left',
            cellClass: column.style?.textAlign === 'right' ? 'justify-end' : undefined,
            width: column.width ?? sizing?.width,
            minWidth: sizing?.minWidth,
            cellRenderer: column.noRowClick
                ? (params: any) => <div data-no-row-click>{renderCell(params)}</div>
                : renderCell,
            flex: sizing?.flex,
        }
    })

// ── Main component ────────────────────────────────────────────────────────────

interface PromotionsTableNewProps {
    hideIfNone?: boolean
    filterOverride?: Record<string, any>
    onSwitchToClassic?: () => void
}

const PromotionsTableNewInner = ({
    hideIfNone = false,
    filterOverride = {},
    onSwitchToClassic,
}: PromotionsTableNewProps): JSX.Element => {
    const navigate = useNavigate()
    const { search } = useLocation()
    const useNavV2 = useMemo(() => isNavV2Enabled(search), [search])
    const { hasActiveSubscription } = useUser()
    const [store] = useState(() => new TableStore<any>(userStore, TableName.Promotions))
    const savedFiltersStore = useSavedFiltersStore()
    const [stats, setStats] = useState<{
        active: number
        paused: number
        ran_last_24h: number
        changed_last_24h: number
    } | null>(null)

    const profile = useSelector<State, EntityId | null>((state) => state.user.profile)
    const load = (params) => {
        if (!profile) {
            return Promise.resolve()
        }
        return axiosGet('promotions', {
            ...params,
            filter: { ...params.filter, ...filterOverride },
        })
    }
    const tableHooks = useTableHooksFromStore(load, {}, false, store, profile)

    useEffect(() => {
        if (!profile) return
        axiosGet('promotions-stats', null, false, { ignore: true })
            .then((res) => setStats(res.data))
            .catch((err) => {
                console.log('Stats endpoint error', err)
                setStats(DEFAULT_STATS)
            })
    }, [profile])

    const currentSort = store.params?.sort ?? ''
    const setSort = (value: string) =>
        store.setParams({ ...store.params, sort: value || undefined, page: 1 })

    const gridColumns = useMemo<AgGridColumnDef<any>[]>(
        () => [
            Columns.Select,
            ...promotionColumnsToAgGrid(
                columns,
                (update) => store.setItems(update),
                tableHooks.reload,
            ),
        ],
        [store, tableHooks.reload],
    )

    return (
        <NewPageLayout
            onSwitchToClassic={onSwitchToClassic}
            toolbarParams={tableHooks.params ?? {}}
            breadcrumbs={[{ label: 'Automate' }]}
            title="Smart Campaigns"
            titleExtra={
                <Tooltip content="Open Smart Campaigns help document">
                    <a
                        href="https://www.merchjar.com/help/docs/smart-campaigns-overview"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full border border-gray-400 text-[#64748B] text-[12px] font-medium leading-none hover:border-gray-600 hover:text-[#0E2947] transition-colors no-underline flex-shrink-0 mt-1"
                        aria-label="Smart Campaigns docs"
                    >
                        <Icon icon="share" size={12} />
                    </a>
                </Tooltip>
            }
            rightActions={
                <Tooltip
                    content={<div>Promotion creation disabled without an active subscription</div>}
                    disabled={hasActiveSubscription}
                >
                    <AnchorButton
                        intent="primary"
                        className="rounded-lg"
                        disabled={!hasActiveSubscription}
                        onClick={() => navigate('/smart-campaigns/new')}
                        icon={<Icon icon="plus" />}
                        text="New Smart Campaign"
                    />
                </Tooltip>
            }
            flatNavV2
        >
            {/* ── Stats cards ── */}
            <div
                className={classNames(
                    'grid grid-cols-4 border-b border-[#E5E7EB]',
                    useNavV2 ? 'h-[49px] divide-x divide-[#E5E7EB]' : 'gap-2 mb-4 pb-4',
                )}
            >
                <StatCard label="Active" value={stats?.active} flat={useNavV2} />
                <StatCard label="Paused" value={stats?.paused} flat={useNavV2} />
                <StatCard label="Ran in last 24h" value={stats?.ran_last_24h} flat={useNavV2} />
                <StatCard
                    label="Changed in last 24h"
                    labelTooltip={LAST_CHANGED_TOOLTIP}
                    value={stats?.changed_last_24h}
                    flat={useNavV2}
                />
            </div>

            {/* ── Controls bar ── */}
            <div
                className={classNames(
                    'flex items-center',
                    useNavV2 ? 'h-[49px] px-2 border-t border-b border-[#E5E7EB]' : 'mb-2.5',
                )}
            >
                <div className="flex items-center gap-2">
                    <Combobox
                        options={SORT_OPTIONS}
                        value={currentSort}
                        onChange={setSort}
                        placeholder="Sort by…"
                        minimal
                    />
                </div>
                <div className="flex items-center gap-2 ml-auto"></div>
            </div>

            {/* Tighten spacing for this table only */}
            <div
                className={classNames(
                    '[&_td]:!py-1 [&_th]:!py-0.5 [&_th>div]:!py-1 [&_td:first-child]:!pl-3 [&_td:first-child]:!cursor-default [&_td:first-child]:!w-[56px] [&_td:first-child]:!max-w-[56px] [&_th:first-child]:!pl-3 [&_th:first-child>div]:!pl-0 [&_th:first-child]:!w-[56px] [&_th:first-child]:!max-w-[56px] [&_#table-bottom-section]:!mt-0 [&_#table-bottom-section]:!mb-1 [&_#table-bottom-section]:!py-1.5 [&_#table-bottom-section]:!items-center',
                    !useNavV2 && '[&_.bp4-card-rounded]:!shadow-[0_0_0_1px_#DBE2E8]',
                )}
            >
                <AgGridTable
                    name={TableName.Promotions}
                    mutableFields={mutableFields}
                    showHeader={false}
                    hideIfNone={hideIfNone}
                    onRowClickItem={(promotion) => navigate(`/smart-campaigns/${promotion.id}`)}
                    tableHooks={tableHooks}
                    store={store}
                    savedFiltersStore={savedFiltersStore}
                    savedFiltersTableName={TableName.Promotions.toLowerCase()}
                    filters={filters}
                    search
                    searchPlaceholder="Search"
                    bulkActions
                    columns={gridColumns}
                    className={useNavV2 ? 'mb-0' : 'mb-4'}
                    selectKey="id"
                    stableRowId
                    dateSelect={false}
                    resizableColumns
                />
            </div>
        </NewPageLayout>
    )
}

export const PromotionsTableNew = observer(PromotionsTableNewInner)

// ── DateCell ──────────────────────────────────────────────────────────────────

const DateCell = ({ date, label }: { date: string | null; label: string }) => {
    const parsed = date ? new Date(date) : null
    return (
        <Tooltip
            position="top"
            content={parsed ? `${label} · ${format(parsed, 'M/d/yy h:mm a')}` : label}
        >
            <span className="text-[14px] text-[#7289A3] cursor-default">
                {parsed ? formatDistanceToNow(parsed, { addSuffix: true }) : 'Never'}
            </span>
        </Tooltip>
    )
}

// ── StatusBadge ───────────────────────────────────────────────────────────────

const StatusBadge = ({ enabled }: { enabled: boolean }) => (
    <Tooltip content={enabled ? 'Active' : 'Paused'} position="top">
        <span
            className={`inline-block w-2 h-2 rounded-full flex-shrink-0 cursor-default ${enabled ? 'bg-emerald-400' : 'bg-[#CBD5E1]'}`}
        ></span>
    </Tooltip>
)

// ── PromotionNameCell ─────────────────────────────────────────────────────────

const PromotionNameCell = ({ promotion, extra }) => (
    <div className="flex items-center gap-2 min-w-0">
        <StatusBadge enabled={promotion.enabled} />
        <PreservingLink
            to={`/smart-campaigns/${promotion.id}`}
            className={classNames(
                'text-[15px] font-[500] tracking-[-0.01em] text-[#0E2947] group-hover:text-[#1A6FD4] transition-colors truncate leading-snug no-underline pointer-events-none',
                { blur: extra.screenshotMode },
            )}
        >
            {promotion.name}
        </PreservingLink>
    </div>
)

// ── Helpers ───────────────────────────────────────────────────────────────────

const toggle = (id, checked, extra) =>
    axiosGet(`promotions/${id}/enabled?enabled=${checked ? 1 : 0}`).then((res) => {
        Toasts.success(`Promotion ${checked ? 'enabled' : 'disabled'}`)
        extra.mutateItems((items) => items.map((i) => (i.id === res.data.id ? res.data : i)))
    })

const PromotionActions = ({ promotion, extra }) => {
    const { hasActiveSubscription } = useUser()
    const { search } = useLocation()

    return (
        <div className="flex justify-end">
            <EllipsisMenu
                content={
                    <Menu>
                        <MenuItem
                            text={promotion.enabled ? 'Disable' : 'Enable'}
                            disabled={!hasActiveSubscription}
                            title={
                                hasActiveSubscription
                                    ? undefined
                                    : 'An active subscription is required to change Smart Campaign status'
                            }
                            onClick={() => {
                                if (hasActiveSubscription) {
                                    toggle(promotion.id, !promotion.enabled, extra)
                                }
                            }}
                        />
                        <a
                            href={dryRunHref(promotion.id, promotion.profile_id, search)}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <MenuItem text="Dry Run" />
                        </a>
                        <PreservingLink
                            to={{
                                pathname: `/logs`,
                                search: `?source_id=${promotion.id}&source_type_id=${EventParentTypes.Promotion}`,
                            }}
                        >
                            <MenuItem text="View Logs" />
                        </PreservingLink>
                        <MenuItem
                            text="Duplicate"
                            onClick={() => {
                                if (
                                    confirm(
                                        `Are you sure you want to duplicate promotion "${promotion.name}"?`,
                                    )
                                ) {
                                    duplicate(promotion.id, extra.reload)
                                }
                            }}
                        />
                        <MenuItem
                            text="Delete"
                            onClick={() => {
                                if (confirm('Are you sure you wish to delete this promotion?')) {
                                    destroy(promotion.id, extra.reload)
                                }
                            }}
                        />
                    </Menu>
                }
            />
        </div>
    )
}

// ── StatCard ──────────────────────────────────────────────────────────────────

const StatCard = ({
    label,
    value,
    labelTooltip,
    flat = false,
}: {
    label: string
    value: number | undefined
    labelTooltip?: React.ReactNode
    flat?: boolean
}) => (
    <div
        className={classNames(
            'bg-white px-3',
            flat
                ? 'h-[49px] py-1.5 flex flex-col justify-center'
                : 'py-2.5 rounded-lg border border-[#DBE2E8]',
        )}
    >
        <div className="text-[12px] text-[#6B7280] mb-0.5">
            {labelTooltip ? (
                <Tooltip content={labelTooltip} position="top">
                    <span className="cursor-default">{label}</span>
                </Tooltip>
            ) : (
                label
            )}
        </div>
        <div className="text-[20px] font-[500] text-[#0E2947] leading-none">
            {value ?? <span className="inline-block w-6 h-4 bg-[#F3F4F6] rounded animate-pulse" />}
        </div>
    </div>
)

const destroy = (id, reload) =>
    axiosDestroy(`promotions/${id}`).then(() => {
        Toasts.success('Promotion deleted')
        reload()
    })

const duplicate = (id, reload) =>
    axiosPost(`promotions/${id}/duplicate`).then(() => {
        Toasts.success('Promotion duplicated')
        reload()
    })
